import React from 'react';
import 'mapbox-gl/dist/mapbox-gl.css';

import setupMapbox from '~utils/map/setupMapbox';
import useMapboxMap from '~utils/map/useMapboxMap';
import useMapPlotsLayer from '~utils/map/useMapPlotsLayer';
import useFetchPlots from '~api/useFetchPlots';
import useSelectPlotEvent from '~utils/map/events/useSelectPlotEvent';
import useHoverPlotEvent from '~utils/map/events/useHoverPlotEvent';
import MapControls from '~molecules/Map/MapControls';
import useReservedPlots from '~utils/plot/useReservedPlots';
import useMapPlotsFlags from '~utils/map/useMapPlotsFlags';
import PlotPreview from '~molecules/PlotPreview';
import useMapPlotsImagesLayers from '~utils/map/useMapPlotsImagesLayers';
import useMapDistrictNamesLayer from '~utils/map/useMapDistrictNamesLayer';
import useMapMaxPitch from '~utils/map/events/useMapMaxPitch';
import usePremiumPlots from '~utils/plot/usePremiumPlots';
import {MapLayer} from '~constants/plots';
import {Loader} from '~components/atoms/Loader';
import NoPlotsFound from '~components/molecules/NoPlotsFound';
import useCenterMap from '~utils/map/useCenterMap';
import useMapSizeController from '~utils/map/useMapSizeController';
import useMapPlotsMockedImagesLayers from '~utils/map/useMapPlotsMockedImagesLayers';

import {
    Background,
    LoaderContainer,
    Map,
    MapContainer,
    NoPlotsFoundContainer,
} from './styles';

setupMapbox();

type MarsMapProps = {
    filteredPlots?: number[] | null;
    isLoading?: boolean;
};

const MarsMap = ({filteredPlots, isLoading}: MarsMapProps) => {
    const {ref, map, loaded} = useMapboxMap();
    const containerRef = useMapSizeController(map);
    const {selected, unselect} = useSelectPlotEvent(map);
    const {data} = useFetchPlots();
    const mapLoaded = Boolean(map.current?.getLayer(MapLayer.HexFill));

    useMapMaxPitch(map);

    useReservedPlots(true);
    usePremiumPlots();

    useMapDistrictNamesLayer(map);

    useMapPlotsFlags(map, data, filteredPlots);
    useMapPlotsLayer(map, data);
    useHoverPlotEvent(map);
    useCenterMap(map, data, filteredPlots, selected);

    useMapPlotsImagesLayers(map);
    useMapPlotsMockedImagesLayers(map);

    return (
        <MapContainer ref={containerRef}>
            <LoaderContainer isLoading={!mapLoaded || Boolean(isLoading)}>
                <Loader bg="transparent" color="#ffffff" />
            </LoaderContainer>
            {mapLoaded && !isLoading && filteredPlots?.length === 0 && (
                <NoPlotsFoundContainer>
                    <NoPlotsFound color="white" />
                </NoPlotsFoundContainer>
            )}

            <Background />
            <Map ref={ref} loaded={loaded} data-cy="map" />
            <MapControls mapRef={map} />
            <PlotPreview id={selected} closeModal={unselect} />
        </MapContainer>
    );
};

export default MarsMap;
